import { useMemo, useState } from 'react';
import { ChevronDown, Eye, EyeOff, ListTree, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { pruneDeleted, type ModelTreeNode } from '@/ifc/modelTree';
import { useViewer } from '@/features/viewer/ViewerContext';
import { useViewerStore } from '@/state/viewerStore';
import { cn } from '@/lib/cn';

export function ModelTreePanel() {
  const tree = useViewerStore((s) => s.tree);
  const treeOpen = useViewerStore((s) => s.treeOpen);
  const setTreeOpen = useViewerStore((s) => s.setTreeOpen);
  const modelName = useViewerStore((s) => s.modelName);
  const deletedIDs = useViewerStore((s) => s.deletedIDs);

  // deleted elements leave the tree entirely, unlike hidden ones
  const visibleTree = useMemo(
    () => (tree === null ? null : pruneDeleted(tree, deletedIDs)),
    [tree, deletedIDs],
  );

  if (!treeOpen || modelName === null) return null;

  return (
    <aside className="pointer-events-auto absolute top-16 bottom-14 left-3 z-30 flex w-72 flex-col overflow-hidden rounded-lg border border-panel-border bg-panel/90 shadow-2xl backdrop-blur">
      <header className="flex items-center justify-between border-b border-panel-border px-3 py-2">
        <div className="flex items-center gap-2">
          <ListTree className="h-4 w-4 text-accent" />
          <span className="text-sm font-semibold">Model</span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setTreeOpen(false)} aria-label="Close model tree">
          <X className="h-4 w-4" />
        </Button>
      </header>
      <div className="flex-1 overflow-y-auto py-1.5 pr-1 pl-1.5">
        {tree === null ? (
          <div className="px-2 py-1 text-xs text-muted">Building structure…</div>
        ) : visibleTree === null ? (
          <div className="px-2 py-1 text-xs text-muted">Every element has been deleted.</div>
        ) : (
          <TreeRow node={visibleTree} depth={0} />
        )}
      </div>
    </aside>
  );
}

function TreeRow({ node, depth }: { node: ModelTreeNode; depth: number }) {
  const [open, setOpen] = useState(node.kind === 'spatial');
  const hiddenIDs = useViewerStore((s) => s.hiddenIDs);
  const selectedID = useViewerStore((s) => s.selection.expressID);
  const { selectElement, setElementsVisible } = useViewer();

  const hasChildren = node.children.length > 0;
  const isElement = node.kind === 'element';
  const isSelected = isElement && node.expressID === selectedID;

  const hiddenCount = node.elementIDs.reduce((n, id) => (hiddenIDs.has(id) ? n + 1 : n), 0);
  const visState =
    node.elementIDs.length === 0 || hiddenCount === 0
      ? 'visible'
      : hiddenCount === node.elementIDs.length
        ? 'hidden'
        : 'partial';

  const onRowClick = (): void => {
    if (isElement && node.expressID !== null) selectElement(node.expressID);
    else if (hasChildren) setOpen((v) => !v);
  };

  const onRowDoubleClick = (): void => {
    if (isElement && node.expressID !== null) selectElement(node.expressID, true);
  };

  return (
    <div>
      <div
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={hasChildren ? open : undefined}
        onClick={onRowClick}
        onDoubleClick={onRowDoubleClick}
        title={isElement ? `${node.label} · double-click to zoom` : node.label}
        className={cn(
          'group flex cursor-pointer items-center gap-1 rounded-md py-1 pr-1 text-xs transition-colors select-none',
          isSelected ? 'bg-accent/20 text-accent' : 'hover:bg-white/6',
          visState === 'hidden' && 'opacity-45',
        )}
        style={{ paddingLeft: `${depth * 14 + 4}px` }}
      >
        {hasChildren ? (
          <ChevronDown
            className={cn('h-3.5 w-3.5 shrink-0 text-muted transition-transform', !open && '-rotate-90')}
          />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <span className={cn('truncate', node.kind !== 'element' && 'font-medium')}>
          {node.label}
        </span>
        {node.kind === 'typeGroup' && (
          <span className="ml-1 shrink-0 rounded bg-white/8 px-1.5 py-px text-[10px] text-muted">
            {node.children.length}
          </span>
        )}
        {node.elementIDs.length > 0 && (
          <button
            type="button"
            aria-label={visState === 'visible' ? `Hide ${node.label}` : `Show ${node.label}`}
            onClick={(e) => {
              e.stopPropagation();
              setElementsVisible(node.elementIDs, visState !== 'visible');
            }}
            className={cn(
              'ml-auto shrink-0 rounded p-0.5 text-muted transition-opacity hover:text-foreground',
              visState === 'visible' && 'opacity-0 group-hover:opacity-100',
            )}
          >
            {visState === 'visible' ? (
              <Eye className="h-3.5 w-3.5" />
            ) : (
              <EyeOff className={cn('h-3.5 w-3.5', visState === 'partial' && 'opacity-60')} />
            )}
          </button>
        )}
      </div>
      {hasChildren && open && (
        <div role="group">
          {node.children.map((child) => (
            <TreeRow key={child.key} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
